import { useState, useEffect, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useMagnum } from '@/context/MagnumContext'
import { apiFetch } from '@/lib/apiFetch'
import { wordSizeClass } from '@/lib/utils'
import { spokenMatches, isHomophoneMatch } from '@/lib/homophones'
import { useSpeechRecognition } from '@/hooks/useSpeechRecognition'
import { useExerciseSession, NO_ATTEMPT_TIMEOUT, SessionCredit } from '@/hooks/useExerciseSession'
import { fetchMagnumMoment, EffectData } from '@/lib/magnumMoment'
import MagnumSays from '@/components/MagnumSays'
import AudioInputMonitor from '@/components/AudioInputMonitor'
import EffectRenderer from '@/components/EffectRenderer'

type PracticeState = 'idle' | 'loading' | 'ready' | 'listening' | 'result' | 'done' | 'timedout'

interface Word {
  id: number
  text: string
  gradeLevel: number
}

const STREAK_MILESTONES = [3, 5, 7, 10]
const STORAGE_KEY = 'exercise_progress_WORD_DECODE'

// Reading a word aloud should land inside half a minute. Past that the kid has
// walked away (or is letting the clock run), so the round stops counting — with
// a visible warning first so it never feels like a rug-pull.
const ATTEMPT_TIMEOUT_MS = 30_000
const ATTEMPT_WARN_MS = 20_000

export default function WordDecodePage() {
  const { user } = useAuth()
  const magnum = useMagnum()
  const [timedOut, setTimedOut] = useState(false)
  const [credit, setCredit] = useState<SessionCredit | null>(null)
  const { sessionId, stats, activeMs, isIdle, msSinceAttempt, startSession, endSession, recordAttempt, dismissIdle, touchActivity, restoreSession, persist, clearPersisted } = useExerciseSession({
    exerciseType: 'WORD_DECODE',
    attemptTimeoutMs: ATTEMPT_TIMEOUT_MS,
    onAttemptTimeout: () => setTimedOut(true),
  })
  // Reading into the mic fires no pointer/keyboard events; feed speech activity
  // into the session so an engaged reader isn't flagged idle.
  const { isListening, isSupported, result, error: speechError, startListening, stopListening } = useSpeechRecognition({ onActivity: touchActivity })

  const [state, setState] = useState<PracticeState>('idle')
  const [words, setWords] = useState<Word[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [lastResult, setLastResult] = useState<{ correct: boolean } | null>(null)
  const [activeEffects, setActiveEffects] = useState<EffectData[] | null>(null)
  const [wordStartTime, setWordStartTime] = useState(0)

  const currentWord = words[currentIndex] || null

  // Sync idle state to Magnum frame
  useEffect(() => {
    magnum.setFrameIdle(isIdle)
    if (isIdle) {
      fetchMagnumMoment({ contextType: 'IDLE_RETURN', exerciseType: 'WORD_DECODE',
      category: 'LANGUAGE', streak: stats.streak, sessionId, activeMs })
        .then((m) => magnum.handleEngineResponse(m))
        .catch(() => magnum.say("Still here. Still waiting. Still adorable."))
    }
  }, [isIdle])

  const persistProgress = (ws: Word[], idx: number) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ words: ws, currentIndex: idx }))
    } catch {}
    persist()
  }

  const startPractice = async () => {
    setState('loading')
    setTimedOut(false)
    setCredit(null)
    try {
      const [wordData] = await Promise.all([
        apiFetch('/words/practice?size=10'),
        startSession(),
      ])
      setWords(wordData)
      setCurrentIndex(0)
      setLastResult(null)
      setState('ready')
      setWordStartTime(Date.now())
      persistProgress(wordData, 0)

      fetchMagnumMoment({ contextType: 'GAME_STARTED', exerciseType: 'WORD_DECODE',
      category: 'LANGUAGE', streak: 0, sessionId })
        .then((m) => magnum.handleEngineResponse(m, 'happy'))
        .catch(() => {})
    } catch {
      setState('idle')
    }
  }

  const handleRecordAttempt = useCallback(async (correct: boolean, responseMs: number) => {
    if (!currentWord) return

    const newStreak = correct ? stats.streak + 1 : 0
    const isMilestone = correct && STREAK_MILESTONES.includes(newStreak)
    const contextType = correct
      ? (isMilestone ? 'STREAK_MILESTONE' : 'CORRECT_ANSWER')
      : 'WRONG_ANSWER'

    setLastResult({ correct })
    setState('result')

    // What Tim says when the engine has nothing for this moment. A kid who was
    // misheard, or who was right in sound but not spelling, has earned an
    // explanation rather than a bare cross.
    const heard = result?.transcript
    let fallback: string | null = null
    if (correct && heard && isHomophoneMatch(heard, currentWord.text)) {
      fallback = `"${heard.trim()}" and "${currentWord.text}" sound exactly alike. Your ear was right, and that is the harder half.`
    } else if (!correct && heard) {
      fallback = `I heard "${heard.trim()}". The word was "${currentWord.text}".`
    }

    fetchMagnumMoment({
      contextType,
      exerciseType: 'WORD_DECODE',
      category: 'LANGUAGE',
      wordId: currentWord.id,
      wordText: currentWord.text,
      responseGiven: result?.transcript ?? undefined,
      correct,
      responseMs,
      activeMs,
      streak: newStreak,
      sessionId,
    }).then((moment) => {
      if (moment.line) {
        magnum.handleEngineResponse(moment, correct ? 'happy' : 'encouraging')
      } else if (fallback) {
        magnum.say(fallback, correct ? 'happy' : 'encouraging')
      }
      if (moment.effects?.length) setActiveEffects(moment.effects)
    }).catch(() => {
      if (fallback) magnum.say(fallback, correct ? 'happy' : 'encouraging')
    })

    await recordAttempt(currentWord.id, correct, responseMs)
  }, [currentWord, recordAttempt, stats.streak, sessionId, magnum, result])

  const processedResultRef = useRef<string | null>(null)
  useEffect(() => {
    if (!result || !currentWord || state !== 'listening') return
    const resultKey = `${currentWord.id}-${result.transcript}`
    if (processedResultRef.current === resultKey) return
    processedResultRef.current = resultKey

    const responseMs = Date.now() - wordStartTime
    // Speech recognition hears sound, not spelling: a kid who reads "two"
    // correctly is transcribed as "too" or "2" about as often as not.
    const correct = spokenMatches(result.transcript, currentWord.text)
    handleRecordAttempt(correct, responseMs)
  }, [result, currentWord, state, wordStartTime, handleRecordAttempt])

  const nextWord = () => {
    const next = currentIndex + 1
    if (next >= words.length) {
      handleEndSession()
    } else {
      setCurrentIndex(next)
      setLastResult(null)
      setState('ready')
      setWordStartTime(Date.now())
      processedResultRef.current = null
      persistProgress(words, next)
    }
  }

  const handleEndSession = async () => {
    setState('done')
    clearPersisted()
    try { sessionStorage.removeItem(STORAGE_KEY) } catch {}

    fetchMagnumMoment({ contextType: 'SESSION_END', exerciseType: 'WORD_DECODE',
      category: 'LANGUAGE', streak: stats.streak, sessionId, activeMs })
      .then((m) => magnum.handleEngineResponse(m, 'excited'))
      .catch(() => {})

    setCredit(await endSession())
  }

  // Thirty seconds with no answer ends the round, and its time stops counting
  // toward screen time — otherwise leaving the game open is as good as playing.
  useEffect(() => {
    if (!timedOut || state === 'timedout' || state === 'done' || state === 'idle') return
    setState('timedout')
    stopListening()
    clearPersisted()
    try { sessionStorage.removeItem(STORAGE_KEY) } catch {}
    // No line here: the stopped-round panel says this through Tim already, and
    // its message wins over anything said now.
    endSession(NO_ATTEMPT_TIMEOUT).then(setCredit)
  }, [timedOut])

  // Warn before the cut so the timeout is never a surprise.
  const warnedRef = useRef(false)
  useEffect(() => {
    const inPlay = state === 'ready' || state === 'listening'
    if (!inPlay || msSinceAttempt < ATTEMPT_WARN_MS) {
      if (msSinceAttempt < ATTEMPT_WARN_MS) warnedRef.current = false
      return
    }
    if (warnedRef.current) return
    warnedRef.current = true
    magnum.say("Still with me? Give the word a try — say it out loud or tap Skip.", 'encouraging')
  }, [msSinceAttempt, state])

  useEffect(() => {
    if (state === 'result') {
      const timer = setTimeout(nextWord, 1500)
      return () => clearTimeout(timer)
    }
  }, [state, currentIndex])

  useEffect(() => {
    if (state === 'ready' && isSupported) {
      setState('listening')
      setWordStartTime(Date.now())
      processedResultRef.current = null
      startListening()
    }
  }, [state, isSupported, startListening])

  const handleSkip = () => {
    if (currentWord) {
      const responseMs = Date.now() - wordStartTime
      handleRecordAttempt(false, responseMs)
    }
  }

  useEffect(() => {
    if (state === 'idle') {
      const restored = restoreSession()
      if (restored) {
        try {
          const raw = sessionStorage.getItem(STORAGE_KEY)
          if (raw) {
            const data = JSON.parse(raw)
            if (data.words?.length > 0) {
              setWords(data.words)
              setCurrentIndex(data.currentIndex || 0)
              setState('ready')
              setWordStartTime(Date.now())
              return
            }
          }
        } catch {}
      }
      startPractice()
    }
  }, [])

  // Dismiss idle on user interaction
  useEffect(() => {
    if (isIdle && (state === 'listening' || state === 'result')) {
      dismissIdle()
    }
  }, [state])

  if (!user || user.type !== 'child') return null

  if (state === 'loading') {
    return (
      <div className="py-16 text-center">
        <p className="text-lg text-muted-foreground">Loading words...</p>
      </div>
    )
  }

  if (state === 'timedout') {
    return (
      <div className="py-8 space-y-8 max-w-sm mx-auto">
        <div className="text-6xl text-center">⏸️</div>
        <h1 className="text-3xl font-bold text-center">Round stopped</h1>
        <MagnumSays variant="warning">
          You went a while without trying a word, so I stopped the round. The
          clock only counts while you're actually reading — start a new round
          whenever you're ready! 📚
        </MagnumSays>
        <div className="flex gap-4 justify-center pt-2">
          <button
            onClick={startPractice}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-bold hover:opacity-90 transition min-h-[44px]"
          >
            Start a new round
          </button>
          <Link
            to="/practice"
            className="px-6 py-3 border border-border rounded-xl font-bold hover:bg-accent transition inline-flex items-center min-h-[44px]"
          >
            Back to Exercises
          </Link>
        </div>
      </div>
    )
  }

  if (state === 'done') {
    const accuracy = stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0
    return (
      <div className="py-8 text-center space-y-8">
        <div className="text-6xl">{credit && !credit.counted ? '📚' : '🎉'}</div>
        <h1 className="text-3xl font-bold">Session Complete!</h1>
        {credit && !credit.counted && (
          <div className="max-w-sm mx-auto text-left">
            <MagnumSays variant="warning">
              {credit.reason === 'BELOW_ACCURACY'
                ? "This one doesn't count toward your screen time — you got under half the words. Take your time and sound them out, and the next round will count! 💪"
                : "This one doesn't count toward your screen time — I need at least five words tried before it counts. Give it another go! 💪"}
            </MagnumSays>
          </div>
        )}
        <div className="grid grid-cols-3 gap-4 max-w-sm mx-auto">
          <div className="p-4 border border-border rounded-lg">
            <p className="text-2xl font-bold text-primary">{stats.correct}</p>
            <p className="text-xs text-muted-foreground">Correct</p>
          </div>
          <div className="p-4 border border-border rounded-lg">
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </div>
          <div className="p-4 border border-border rounded-lg">
            <p className="text-2xl font-bold text-primary">{accuracy}%</p>
            <p className="text-xs text-muted-foreground">Accuracy</p>
          </div>
        </div>
        <div className="flex gap-4 justify-center pt-4">
          <button
            onClick={startPractice}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-bold hover:opacity-90 transition"
          >
            Practice More
          </button>
          <Link
            to="/practice"
            className="px-6 py-3 border border-border rounded-xl font-bold hover:bg-accent transition inline-flex items-center"
          >
            Back to Exercises
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="text-center space-y-6">
      <EffectRenderer effects={activeEffects} onDone={() => setActiveEffects(null)} />

      <AudioInputMonitor />

      {/* Top bar */}
      <div className="flex items-center justify-between">
        <Link
          to="/practice"
          className="text-sm text-muted-foreground hover:text-foreground transition"
        >
          &larr; Back
        </Link>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span>{currentIndex + 1} of {words.length}</span>
          <span className="text-primary font-medium">{stats.correct} correct</span>
          {stats.total > 0 && (
            <span>Avg: {(stats.avgResponseMs / 1000).toFixed(1)}s</span>
          )}
          {stats.streak >= 3 && (
            <span className="text-amber-500 font-medium">🔥 {stats.streak}</span>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-accent rounded-full h-2">
        <div
          className="bg-primary h-2 rounded-full transition-all duration-300"
          style={{ width: `${((currentIndex + (state === 'result' ? 1 : 0)) / words.length) * 100}%` }}
        />
      </div>

      {/* Word display */}
      <div className="py-8">
        <p className={`${wordSizeClass(currentWord?.text ?? '', 'decode')} font-black tracking-tight break-words`}>
          {currentWord?.text}
        </p>
      </div>

      {/* The game states the verdict and nothing else — Tim does the talking. */}
      {state === 'result' && lastResult && (
        <p className={`text-2xl font-black ${lastResult.correct ? 'text-primary' : 'text-amber-500'}`}>
          {lastResult.correct ? '✓ Correct' : '✗ Incorrect'}
        </p>
      )}

      {/* Speech error */}
      {speechError && state === 'listening' && (
        <p className="text-sm text-amber-600">{speechError}</p>
      )}

      {/* No-answer countdown — visible before the round stops counting */}
      {(state === 'ready' || state === 'listening') && msSinceAttempt >= ATTEMPT_WARN_MS && (
        <p className="text-sm font-medium text-amber-600">
          Still there? Round stops in {Math.max(0, Math.ceil((ATTEMPT_TIMEOUT_MS - msSinceAttempt) / 1000))}s
        </p>
      )}

      {/* Action buttons */}
      <div className="flex gap-4 justify-center">
        {(state === 'ready' || state === 'listening') && (
          <>
            <div className="px-8 py-4 bg-red-50 border-2 border-red-300 rounded-xl text-lg font-bold text-red-600 animate-pulse min-w-[200px]">
              🎤 Listening...
            </div>
            <button
              onClick={handleSkip}
              className="px-4 py-4 border border-border rounded-xl text-muted-foreground hover:bg-accent transition"
            >
              Skip
            </button>
          </>
        )}
      </div>
    </div>
  )
}
